import type { BrowserLocalCacheCryptoEnvelope } from './types'

const TEXT_ENCODER = new TextEncoder()
const TEXT_DECODER = new TextDecoder()
const DEFAULT_ENCRYPTION_VERSION = 1

type BrowserCacheCryptoContext = {
  userScope: string
  cacheKey: string
  schemaVersion: number
  entityType?: string
  keyId: string
  encryptionVersion?: number
}

const getRuntimeCrypto = (): Crypto | null => {
  const runtimeCrypto = globalThis.crypto
  if (!runtimeCrypto?.subtle || typeof runtimeCrypto.getRandomValues !== 'function') {
    return null
  }
  return runtimeCrypto
}

export const isWebCryptoAvailable = (): boolean => Boolean(getRuntimeCrypto())

const bytesToBase64Url = (bytes: Uint8Array): string => {
  let base64 = ''
  if (typeof btoa === 'function') {
    let binary = ''
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte)
    })
    base64 = btoa(binary)
  } else {
    base64 = Buffer.from(bytes).toString('base64')
  }
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

const base64UrlToBytes = (value: string): Uint8Array => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=')
  if (typeof atob === 'function') {
    const binary = atob(padded)
    const bytes = new Uint8Array(binary.length)
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index)
    }
    return bytes
  }
  return new Uint8Array(Buffer.from(padded, 'base64'))
}

const buildAadString = (context: BrowserCacheCryptoContext): string =>
  JSON.stringify({
    cacheKey: context.cacheKey,
    entityType: context.entityType || '',
    encryptionVersion: context.encryptionVersion || DEFAULT_ENCRYPTION_VERSION,
    keyId: context.keyId,
    schemaVersion: context.schemaVersion,
    userScope: context.userScope,
  })

export const deriveBrowserLocalCacheCryptoKey = async (input: {
  seed: string
  userScope: string
  keyId: string
  encryptionVersion?: number
}): Promise<CryptoKey> => {
  const runtimeCrypto = getRuntimeCrypto()
  if (!runtimeCrypto) {
    throw new Error('Web Crypto is unavailable')
  }
  const baseKey = await runtimeCrypto.subtle.importKey(
    'raw',
    TEXT_ENCODER.encode(input.seed),
    'PBKDF2',
    false,
    ['deriveKey']
  )
  return await runtimeCrypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: TEXT_ENCODER.encode(`${input.userScope}:${input.keyId}:${input.encryptionVersion || DEFAULT_ENCRYPTION_VERSION}`),
      iterations: 120_000,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

export const encryptLocalCacheBytes = async (
  bytes: Uint8Array,
  key: CryptoKey,
  context: BrowserCacheCryptoContext
): Promise<BrowserLocalCacheCryptoEnvelope> => {
  const runtimeCrypto = getRuntimeCrypto()
  if (!runtimeCrypto) {
    throw new Error('Web Crypto is unavailable')
  }
  const iv = runtimeCrypto.getRandomValues(new Uint8Array(12))
  const aadString = buildAadString(context)
  const encrypted = await runtimeCrypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
      additionalData: TEXT_ENCODER.encode(aadString),
    },
    key,
    bytes
  )
  return {
    algorithm: 'AES-GCM',
    encryptionVersion: context.encryptionVersion || DEFAULT_ENCRYPTION_VERSION,
    keyId: context.keyId,
    iv: bytesToBase64Url(iv),
    aad: bytesToBase64Url(TEXT_ENCODER.encode(aadString)),
    cipherText: bytesToBase64Url(new Uint8Array(encrypted)),
  }
}

export const decryptLocalCacheBytes = async (
  envelope: BrowserLocalCacheCryptoEnvelope,
  key: CryptoKey
): Promise<Uint8Array> => {
  const runtimeCrypto = getRuntimeCrypto()
  if (!runtimeCrypto) {
    throw new Error('Web Crypto is unavailable')
  }
  const decrypted = await runtimeCrypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: base64UrlToBytes(envelope.iv),
      additionalData: base64UrlToBytes(envelope.aad),
    },
    key,
    base64UrlToBytes(envelope.cipherText)
  )
  return new Uint8Array(decrypted)
}

export const encryptLocalCacheJson = async (
  payload: unknown,
  key: CryptoKey,
  context: BrowserCacheCryptoContext
): Promise<BrowserLocalCacheCryptoEnvelope> =>
  encryptLocalCacheBytes(TEXT_ENCODER.encode(JSON.stringify(payload)), key, context)

export const decryptLocalCacheJson = async <TPayload>(
  envelope: BrowserLocalCacheCryptoEnvelope,
  key: CryptoKey
): Promise<TPayload> => {
  const bytes = await decryptLocalCacheBytes(envelope, key)
  return JSON.parse(TEXT_DECODER.decode(bytes)) as TPayload
}

